import * as React from 'react'

import { useBgColorCtx } from '~theme/BgColorProvider'
import { Box, Flex, Grid } from '~ui/components/layout/Box'
import { Stack } from '~ui/components/layout/Stack'
import { Skeleton } from '~ui/components/loadingSkeletons/Skeleton'

import { INPUT_GROUP_DIVIDER_CONTRAST } from '../accessories/formAccessories.config'
import { AddAmountButton } from './AddAmountButton'

type TAddAmountButtonProps = Pick<PropsOf<typeof AddAmountButton>, 'isDisabled' | 'canAdd'>

type TBaseOption = {
  amount: number
}

type TProps<TOption extends TBaseOption> = NoChildren &
  SkeletonProps &
  TAddAmountButtonProps & {
    /**
     * if true, render bottom border line under each item
     */
    hasDivider?: boolean
    /**
     * array of options that the add amount buttons and labels should be rendered for
     */
    options: RoA<TOption>
    /**
     * vertical space between individual items
     */
    spacing: PropsOf<typeof Stack>['spacing']
    /**
     * callback on any change of amount (of any of the items)
     */
    onChange: (newAddons: RoA<TOption>) => void
    /**
     * maximum amount of each individual item
     */
    maxIndividualCount?: PropsOf<typeof AddAmountButton>['maxValue']
    /**
     * option label renderer
     */
    renderOptionLabel: (option: TOption) => Defined<React.ReactNode>
    /**
     * button label renderer
     */
    renderButtonLabel: (option: TOption) => Defined<React.ReactNode>
    /**
     * prop of the option that will return string/number id, unique amongst the array
     * used as a key AND for comparing with selected option
     */
    idPropKey: keyof TOption
  }

export const AddAmountButtonGroup = <TOption extends TBaseOption>({
  options,
  hasDivider,
  spacing = `medium`,
  onChange,
  maxIndividualCount,
  renderOptionLabel,
  renderButtonLabel,
  idPropKey,
  canAdd = true,
  isDisabled = false,
  showSkeleton = false,
}: TProps<TOption>) => {
  const { dividerColors } = useBgColorCtx()

  return (
    <Grid
      gridTemplateColumns={`repeat(auto-fill, minmax(300px, 1fr))`}
      columnGap={`large`}
      rowGap={spacing}
    >
      {options.map((option) => (
        <Grid
          id={String(option[idPropKey])}
          gridTemplateColumns={`1fr min-content`}
          key={String(option[idPropKey])}
          borderBottom={
            hasDivider ? `1px solid ${dividerColors[INPUT_GROUP_DIVIDER_CONTRAST]}` : `none`
          }
          padding={{
            right: `none`,
            left: `none`,
            top: `none`,
            bottom: hasDivider ? spacing : `none`,
          }}
        >
          <Box maxWidth={`100%`} transition={`max-width 0.2s`}>
            {renderOptionLabel(option)}
          </Box>
          <Flex alignItems={`flex-start`}>
            <Skeleton showSkeleton={showSkeleton} maxWidth={`5rem`} alignSelf={`end`}>
              <AddAmountButton
                minWidth={`5rem`}
                canAdd={canAdd}
                value={option.amount}
                maxValue={maxIndividualCount}
                isDisabled={isDisabled}
                onChange={(value) => {
                  const newAddons = options.map((opt) => {
                    return option[idPropKey] === opt[idPropKey]
                      ? { ...opt, amount: value } //
                      : opt
                  })

                  onChange(newAddons)
                }}
              >
                {renderButtonLabel(option)}
              </AddAmountButton>
            </Skeleton>
          </Flex>
        </Grid>
      ))}
    </Grid>
  )
}
